/**
 * The company's own boilerplate for new proposals, or for new contracts —
 * `kind` picks which. Settings mounts this component twice.
 *
 * Same editor either way, with the money taken out. There is no document here
 * yet, so the investment table and the payment schedule have no figures to
 * render — they appear as markers saying where those tables will go when a
 * document is built. Everything else is the prose a business actually has an
 * opinion about: scope headings, exclusions, and above all the terms and
 * conditions — which for a contract is the language that actually binds a
 * client, not the proposal's own wording.
 *
 * Editing this changes what the *next* document of that kind starts as, and
 * nothing else. A document already written keeps the words it was written
 * with, which is the same rule the built-ins have always followed — a
 * document someone may have already sent must not change under them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { EMPTY_DOC, type ProseDoc, RichText } from "../RichText";
import { ApiError, api, rateFromPercent, rateToPercent, unwrap } from "../api";
import { Button, ErrorNote, Field, Loading, Panel, input } from "../ui";
import { newKey } from "./contract-parts";

type BlockKind = "prose" | "investment" | "payment_schedule" | "acceptance";

interface BlockDraft {
  key: number;
  kind: BlockKind;
  title: string;
  body: ProseDoc | null;
}

interface ScheduleDraft {
  key: number;
  name: string;
  /** Held as typed — "50", not "0.5". Converted on save. */
  percent: string;
}

const KIND_LABELS: Record<BlockKind, string> = {
  prose: "Text",
  investment: "Investment table",
  payment_schedule: "Payment schedule",
  acceptance: "Signature block",
};

/** What each data block will become, said plainly — there is nothing to render
 *  until a proposal gives these numbers to work with. */
const KIND_NOTES: Record<Exclude<BlockKind, "prose">, string> = {
  investment: "Every part of the job and what it costs, from the proposal's own lines.",
  payment_schedule:
    "The draw schedule below, one table per part of the job. A proposal covering a " +
    "kitchen and a closet gets two.",
  acceptance: "Where the client signs, with your legal name and EIN beneath it.",
};

const DATA_KINDS = ["investment", "payment_schedule", "acceptance"] as const;

const KIND_COPY = {
  proposal: {
    panelTitle: "Proposal template",
    blurb: "What a new proposal starts as. Editing it changes the next proposal you create",
    validHint: "How long a proposal stands before it's re-priced.",
    scheduleLede: "Each part of a new proposal starts with these draws.",
    scheduleUnit: "of the proposal",
  },
  contract: {
    panelTitle: "Contract template",
    blurb:
      "What a new contract starts as when a proposal is converted. Editing it changes the " +
      "next contract you convert",
    validHint:
      "How long a contract stands before it needs re-issuing — most shops leave this long.",
    scheduleLede: "Each part of a new contract starts with these draws.",
    scheduleUnit: "of the contract",
  },
} as const;

export function ProposalTemplatePanel({
  slug,
  kind,
  isAdmin,
}: {
  slug: string;
  kind: "proposal" | "contract";
  isAdmin: boolean;
}) {
  const copy = KIND_COPY[kind];
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [tagline, setTagline] = useState("");
  const [validDays, setValidDays] = useState("60");
  const [blocks, setBlocks] = useState<BlockDraft[]>([]);
  const [schedule, setSchedule] = useState<ScheduleDraft[]>([]);

  const q = useQuery({
    queryKey: ["contract-template", slug, kind],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"]["contract-template"].$get({ param: { slug }, query: { kind } }),
      ),
  });

  // Loaded once per fetch, guarded on `dirty` so a refetch can't discard
  // something half-written — the same shape every other edit form here uses.
  const loaded = q.data;
  useEffect(() => {
    if (!loaded || dirty) return;
    const t = loaded.template;
    setTagline(t.tagline ?? "");
    setValidDays(String(t.validDays));
    setBlocks(
      t.blocks.map((b) => ({
        key: newKey(),
        kind: b.kind as BlockKind,
        title: b.title ?? "",
        body: (b.body as ProseDoc | null) ?? null,
      })),
    );
    setSchedule(
      t.schedule.map((r) => ({ key: newKey(), name: r.name, percent: rateToPercent(r.percent) })),
    );
  }, [loaded, dirty]);

  function touch() {
    setDirty(true);
    setError(null);
  }

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"]["contract-template"].$put({
          param: { slug },
          query: { kind },
          json: {
            tagline,
            validDays: Number(validDays),
            sections: blocks.map((b) => ({
              kind: b.kind,
              title: b.title,
              body: b.kind === "prose" ? (b.body ?? EMPTY_DOC) : null,
            })),
            schedule: schedule.map((r) => ({
              name: r.name,
              percent: rateFromPercent(r.percent),
            })),
          } as never,
        }),
      ),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["contract-template", slug, kind] });
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Couldn't save the template. Try again."),
  });

  const reset = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"]["contract-template"].$delete({
          param: { slug },
          query: { kind },
        }),
      ),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["contract-template", slug, kind] });
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Couldn't restore the built-in template."),
  });

  function move(key: number, dir: -1 | 1) {
    const i = blocks.findIndex((b) => b.key === key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setBlocks(next);
    touch();
  }

  function addBlock(kind: BlockKind) {
    setBlocks([
      ...blocks,
      {
        key: newKey(),
        kind,
        title: kind === "prose" ? "" : KIND_LABELS[kind],
        body: kind === "prose" ? EMPTY_DOC : null,
      },
    ]);
    touch();
  }

  // A data block appears exactly once, so it can only be added back when it is
  // missing. The API enforces the same rule — this only keeps the button from
  // offering something that would be refused.
  const missing = DATA_KINDS.filter((k) => !blocks.some((b) => b.kind === k));

  const percentTotal = schedule.reduce((sum, r) => sum + (Number(r.percent) || 0), 0);
  const totalIsRight = Math.round(percentTotal * 100) === 10_000;

  if (q.isPending) return <Loading />;

  return (
    <Panel
      title={copy.panelTitle}
      action={
        isAdmin ? (
          <div className="flex items-center gap-2">
            {!loaded?.template.isDefault && (
              <Button
                variant="ghost"
                onClick={() => {
                  if (confirm("Discard your template and go back to the built-in one?")) {
                    reset.mutate();
                  }
                }}
                disabled={reset.isPending || save.isPending}
              >
                Restore built-in
              </Button>
            )}
            <Button
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending || reset.isPending}
            >
              {save.isPending ? "Saving…" : "Save template"}
            </Button>
          </div>
        ) : null
      }
    >
      <p className="mb-4 text-sm text-neutral-500">
        {copy.blurb} — never one already written.{" "}
        {loaded?.template.isDefault && (
          <span className="text-neutral-400">
            You're on the built-in template; saving makes it yours.
          </span>
        )}
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <Field label="Tagline">
          <input
            className={input}
            value={tagline}
            disabled={!isAdmin}
            placeholder="Shown under the heading on every document"
            onChange={(e) => {
              setTagline(e.target.value);
              touch();
            }}
          />
        </Field>
        <Field label="Valid for (days)" hint={copy.validHint}>
          <input
            className={input}
            type="number"
            min={1}
            max={365}
            value={validDays}
            disabled={!isAdmin}
            onChange={(e) => {
              setValidDays(e.target.value);
              touch();
            }}
          />
        </Field>
      </div>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        The document
      </h3>
      <div className="mb-6 flex flex-col gap-3">
        {blocks.map((block, i) => (
          <div
            key={block.key}
            className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm"
          >
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                {KIND_LABELS[block.kind]}
              </span>
              <input
                className={`${input} flex-1 py-1 font-medium`}
                value={block.title}
                disabled={!isAdmin}
                placeholder="Heading (optional)"
                onChange={(e) => {
                  setBlocks(
                    blocks.map((b) => (b.key === block.key ? { ...b, title: e.target.value } : b)),
                  );
                  touch();
                }}
              />
              {isAdmin && (
                <div className="flex items-center">
                  <Button variant="ghost" onClick={() => move(block.key, -1)} disabled={i === 0}>
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => move(block.key, 1)}
                    disabled={i === blocks.length - 1}
                  >
                    ↓
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setBlocks(blocks.filter((b) => b.key !== block.key));
                      touch();
                    }}
                  >
                    ×
                  </Button>
                </div>
              )}
            </div>

            {block.kind === "prose" ? (
              <RichText
                value={block.body ?? EMPTY_DOC}
                editable={isAdmin}
                onChange={(body) => {
                  setBlocks(blocks.map((b) => (b.key === block.key ? { ...b, body } : b)));
                  touch();
                }}
              />
            ) : (
              <p className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-500">
                {KIND_NOTES[block.kind]}
              </p>
            )}
          </div>
        ))}
      </div>

      {isAdmin && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={() => addBlock("prose")}>
            + Text block
          </Button>
          {missing.map((kind) => (
            <Button key={kind} variant="ghost" onClick={() => addBlock(kind)}>
              + {KIND_LABELS[kind]}
            </Button>
          ))}
        </div>
      )}

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Default payment schedule
      </h3>
      <p className="mb-3 text-sm text-neutral-500">
        {copy.scheduleLede} Percentages, so they re-split correctly whatever the job turns out to be
        worth.
      </p>

      <div className="flex flex-col gap-2">
        {schedule.map((row) => (
          <div key={row.key} className="flex items-center gap-2">
            <input
              className={`${input} flex-1`}
              value={row.name}
              disabled={!isAdmin}
              placeholder="Deposit / contract signing"
              onChange={(e) => {
                setSchedule(
                  schedule.map((r) => (r.key === row.key ? { ...r, name: e.target.value } : r)),
                );
                touch();
              }}
            />
            <div className="flex w-28 items-center gap-1">
              <input
                className={`${input} text-right`}
                value={row.percent}
                disabled={!isAdmin}
                inputMode="decimal"
                onChange={(e) => {
                  setSchedule(
                    schedule.map((r) =>
                      r.key === row.key ? { ...r, percent: e.target.value } : r,
                    ),
                  );
                  touch();
                }}
              />
              <span className="text-sm text-neutral-500">%</span>
            </div>
            {isAdmin && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSchedule(schedule.filter((r) => r.key !== row.key));
                  touch();
                }}
              >
                ×
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        {isAdmin && (
          <Button
            variant="ghost"
            onClick={() => {
              setSchedule([...schedule, { key: newKey(), name: "", percent: "" }]);
              touch();
            }}
          >
            + Payment
          </Button>
        )}
        {/* Shown as you type rather than only on save: a schedule that doesn't
            reach 100% produces proposals that approval will later refuse. */}
        <span className={`text-sm ${totalIsRight ? "text-neutral-500" : "text-red-600"}`}>
          {percentTotal.toFixed(2)}% {copy.scheduleUnit}
          {totalIsRight ? "" : " — must total 100%"}
        </span>
      </div>
    </Panel>
  );
}
